import { defaultContent, defaultGlobalSettings, GOOGLE_RATING } from './defaultContent';
import type { SiteContent, GlobalSettings } from '../types/cms';
import type { CmsPage } from '../types/cmsPages';

// Only public website data belongs in the build snapshot. Never include auth or leads.
export interface ContentSnapshot {
  sections: { section_key: string; content: Record<string, unknown> }[];
  settings: { setting_key: string; setting_value: string }[];
  pages: CmsPage[];
}

export function resolveContent(snapshot?: ContentSnapshot) {
  const content = { ...defaultContent };
  for (const row of snapshot?.sections ?? []) {
    if (Object.prototype.hasOwnProperty.call(defaultContent, row.section_key) && row.content && typeof row.content === 'object') {
      const key = row.section_key as keyof SiteContent;
      Object.assign(content, { [key]: { ...defaultContent[key], ...row.content } });
    }
  }
  content.hero = {
    ...content.hero,
    ratingText: `${GOOGLE_RATING} on Google`,
    stats: { ...content.hero.stats, googleRating: GOOGLE_RATING },
  };
  content.about = { ...content.about, rating: GOOGLE_RATING };
  content.plansGrid = { ...content.plansGrid, statsGoogleRating: GOOGLE_RATING };
  content.contactInfo = {
    ...content.contactInfo,
    stats: content.contactInfo.stats.map(stat =>
      /google\s+rating/i.test(stat.label) ? { ...stat, value: GOOGLE_RATING } : stat
    ),
  };
  const settings = { ...defaultGlobalSettings };
  for (const row of snapshot?.settings ?? []) {
    if (Object.prototype.hasOwnProperty.call(defaultGlobalSettings, row.setting_key)) {
      settings[row.setting_key as keyof GlobalSettings] = row.setting_value;
    }
  }
  return { content, settings };
}

async function readPublicTable(table: string, query: string) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/${table}?${query}`, {
      headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY },
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Unable to read public CMS ${table}: HTTP ${response.status}`);
    return await response.json();
  } finally {
    clearTimeout(timeout);
  }
}

export async function fetchContentSnapshot(): Promise<ContentSnapshot> {
  const [sections, settings, pages] = await Promise.all([
    readPublicTable('site_content', 'select=section_key,content'),
    readPublicTable('global_settings', 'select=setting_key,setting_value'),
    readPublicTable('cms_pages', 'select=id,type,title,slug,status,content,meta_title,meta_description,created_at,updated_at&status=eq.published&order=created_at.desc'),
  ]);
  const snapshot: ContentSnapshot = { sections, settings, pages };
  const { content } = resolveContent(snapshot);
  // Publish current ratings in the serialized HTML as well as in the rendered page.
  snapshot.sections = snapshot.sections.map(row => {
    if (row.section_key === 'hero' || row.section_key === 'about' || row.section_key === 'plansGrid' || row.section_key === 'contactInfo') {
      return { ...row, content: { ...content[row.section_key] } };
    }
    return row;
  });
  return snapshot;
}
